// IDE deep link builders — generates URLs/snippets to open SatuTim API
// endpoint context inside popular IDEs (Cursor, VS Code, Claude Code CLI, Zed).
//
// Strategy:
//   - Cursor exposes a `cursor://anysphere.cursor-deeplink/prompt?text=...`
//     scheme that opens its agent chat preloaded with text.
//   - VS Code has no first-party prompt scheme. We return null for the URL
//     and let the caller fall back to clipboard-copy of a contextual snippet.
//   - Claude Code is terminal-first, so we generate a ready-to-paste cURL.
//   - Zed has limited deep link support; we attempt `zed://` best-effort and
//     let the caller fall back to clipboard.

import { prefixApiPath } from './api-url';

const DEFAULT_BASE_URL = 'https://api.satutim.id';

export interface DeeplinkInput {
    method: string;
    path: string;
    summary: string;
    apiKey?: string | null;
    baseUrl?: string;
}

/**
 * Generic contextual prompt — used by AI-aware IDEs (Cursor, Zed) to
 * pre-fill the chat panel with an actionable request.
 */
export function buildContextualPrompt({
    method,
    path,
    summary,
    apiKey,
    baseUrl = DEFAULT_BASE_URL,
}: DeeplinkInput): string {
    const keyPlaceholder = apiKey && apiKey.trim().length > 0 ? apiKey : '<API_KEY>';
    return `Tolong bantu saya integrate endpoint SatuTim API ini:

${method.toUpperCase()} ${baseUrl}${prefixApiPath(path)}
Summary: ${summary}

Auth: Bearer ${keyPlaceholder}

Tolong:
1. Buatkan TypeScript function untuk panggil endpoint ini
2. Handle errors umum (401, 404, 500)
3. Type-safe response parsing

Docs lengkap: https://playground.satutim.id (cari operationId yang sesuai)`;
}

/**
 * Cursor deep link — opens Cursor agent with prompt prefilled.
 * Spec: `cursor://anysphere.cursor-deeplink/prompt?text=<encoded>`
 */
export function buildCursorUrl(input: DeeplinkInput): string {
    const prompt = buildContextualPrompt(input);
    return `cursor://anysphere.cursor-deeplink/prompt?text=${encodeURIComponent(prompt)}`;
}

/**
 * VS Code — no reliable prompt-prefill scheme exists. Return null so the
 * caller knows to fall back to "copy snippet" UX.
 */
export function buildVscodeUrl(_input: DeeplinkInput): string | null {
    return null;
}

/**
 * VS Code fallback snippet — a TypeScript-flavoured comment block that the
 * user can paste into any editor; AI assistants (Copilot, Cody) will pick it
 * up as context.
 */
export function buildVscodeSnippet({
    method,
    path,
    summary,
    apiKey,
    baseUrl = DEFAULT_BASE_URL,
}: DeeplinkInput): string {
    const keyPlaceholder = apiKey && apiKey.trim().length > 0 ? apiKey : '<API_KEY>';
    const prefixed = prefixApiPath(path);
    return `// SatuTim API — ${method.toUpperCase()} ${prefixed}
// ${summary}
//
// TODO: implement a typed fetcher for this endpoint.
//   - URL:    ${baseUrl}${prefixed}
//   - Auth:   Authorization: Bearer ${keyPlaceholder}
//   - Errors: handle 401 / 404 / 500
//
// Docs: https://playground.satutim.id

export async function call${toIdentifier(method, path)}(/* params */) {
    const res = await fetch('${baseUrl}${prefixed}', {
        method: '${method.toUpperCase()}',
        headers: {
            'Authorization': 'Bearer ${keyPlaceholder}',
            'Content-Type': 'application/json',
        },
        // body: JSON.stringify({ /* ... */ }),
    });
    if (!res.ok) {
        throw new Error(\`SatuTim API \${res.status}: \${await res.text()}\`);
    }
    return res.json();
}
`;
}

/**
 * Claude Code CLI snippet — ready-to-paste cURL for terminal-based agent.
 */
export function buildClaudeCodeSnippet({
    method,
    path,
    apiKey,
    baseUrl = DEFAULT_BASE_URL,
}: DeeplinkInput): string {
    const keyPlaceholder = apiKey && apiKey.trim().length > 0 ? apiKey : '<API_KEY>';
    const upper = method.toUpperCase();
    const needsBody = upper === 'POST' || upper === 'PUT' || upper === 'PATCH';
    const bodyLine = needsBody ? ` \\\n  -d '{ /* your body */ }'` : '';
    return `# Run di Claude Code terminal:
curl -X ${upper} '${baseUrl}${prefixApiPath(path)}' \\
  -H 'Authorization: Bearer ${keyPlaceholder}' \\
  -H 'Content-Type: application/json'${bodyLine}`;
}

/**
 * Zed — best-effort deep link. Zed's URL scheme support is evolving; this
 * may silently fail in older versions. Caller should still offer a clipboard
 * fallback.
 */
export function buildZedUrl(input: DeeplinkInput): string | null {
    const prompt = buildContextualPrompt(input);
    // Zed's URL scheme is limited; try a generic open with prompt as query.
    // If Zed isn't installed or the scheme is unsupported, the browser will
    // surface that error and the caller should have a clipboard fallback ready.
    return `zed://open?prompt=${encodeURIComponent(prompt)}`;
}

// --- helpers ----------------------------------------------------------------

/** Turn an HTTP method + path into a camelCase identifier suggestion. */
function toIdentifier(method: string, path: string): string {
    const segs = path
        .split('/')
        .filter(Boolean)
        .map((s) => s.replace(/[{}]/g, ''))
        .filter((s) => s.length > 0)
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1));
    const base = segs.join('') || 'Endpoint';
    const verb = method.toLowerCase();
    const verbCap = verb.charAt(0).toUpperCase() + verb.slice(1);
    return `${verbCap}${base}`.replace(/[^A-Za-z0-9_]/g, '');
}
